import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { MathBlock } from "../components/MathRender";
import { VerdictLabel } from "../components/StatusBadge";
import { Scale, ArrowLeft } from "lucide-react";

function CaseDetail({ caseId, onBack, onDecided }) {
  const [c, setC] = useState(null);
  const [decision, setDecision] = useState("uphold_accept");
  const [rationale, setRationale] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    api.get(`/tribunal/cases/${caseId}`).then((r) => setC(r.data)).catch(() => {});
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="py-16 text-center text-slate-500">Loading case…</div>;

  const decide = async () => {
    if (!rationale.trim()) { toast.error("A written rationale is required"); return; }
    setLoading(true);
    try {
      await api.post(`/tribunal/cases/${caseId}/decide`, { decision, rationale });
      toast.success("Binding decision recorded");
      onDecided();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not decide"); }
    finally { setLoading(false); }
  };

  const decided = c.status === "decided";

  return (
    <div data-testid="tribunal-case-detail">
      <button onClick={onBack} className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> All cases</button>
      <h2 className="font-display text-2xl text-slate-900">{c.problem_title}</h2>
      <p className="mt-1 text-sm text-rose-700">{c.reason}</p>

      <div className="axiom-card mt-6 p-5">
        <p className="eyebrow mb-2">Submission under dispute</p>
        <MathBlock>{c.submission?.content_latex}</MathBlock>
      </div>

      <div className="mt-6">
        <p className="eyebrow mb-3">Conflicting reviews (evidence)</p>
        <div className="space-y-3">
          {c.submission?.reviews?.map((r) => (
            <div key={r.id} className="axiom-card p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-900">{r.reviewer_name}</span>
                <span className={`font-mono text-sm ${r.verdict === "accept" ? "text-emerald-700" : "text-rose-700"}`}>{r.verdict.toUpperCase()}</span>
              </div>
              <div className="mt-2"><VerdictLabel label={r.label} /></div>
              {r.comments && <p className="mt-2 text-sm text-slate-500">{r.comments}</p>}
            </div>
          ))}
        </div>
      </div>

      {c.decisions?.length > 0 && (
        <div className="axiom-card mt-6 border-emerald-200 p-5" data-testid="tribunal-decision-record">
          <p className="eyebrow mb-2">Immutable decision record</p>
          {c.decisions.map((d) => (
            <div key={d.id} className="text-sm">
              <p className="text-slate-900">{d.decision === "uphold_accept" ? "Upheld acceptance" : "Upheld rejection"} — {d.adjudicator_name}</p>
              <p className="mt-1 text-slate-500">{d.rationale}</p>
            </div>
          ))}
        </div>
      )}

      {!decided && (
        <div className="axiom-card mt-6 p-6" data-testid="tribunal-decide-panel">
          <h3 className="font-display text-lg text-slate-900">Render a binding decision</h3>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button data-testid="decide-accept" onClick={() => setDecision("uphold_accept")} className={`rounded-lg border px-4 py-3 text-sm ${decision === "uphold_accept" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-[#CBD5E1] text-slate-600"}`}>Uphold acceptance</button>
            <button data-testid="decide-reject" onClick={() => setDecision("uphold_reject")} className={`rounded-lg border px-4 py-3 text-sm ${decision === "uphold_reject" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-[#CBD5E1] text-slate-600"}`}>Uphold rejection</button>
          </div>
          <textarea data-testid="decide-rationale" rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Written rationale (required, part of the immutable record)"
            className="mt-4 w-full rounded-lg border border-[#CBD5E1] bg-slate-50 px-3 py-2 text-sm text-slate-700 outline-none focus:border-[#E61A27]" />
          <button data-testid="decide-submit" onClick={decide} disabled={loading} className="btn-crimson mt-4">{loading ? "Recording…" : "Record binding decision"}</button>
        </div>
      )}
    </div>
  );
}

export default function Tribunal() {
  const [cases, setCases] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    api.get("/tribunal/cases").then((r) => setCases(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8" data-testid="tribunal-page">
      {active ? (
        <CaseDetail caseId={active} onBack={() => { setActive(null); load(); }} onDecided={() => { setActive(null); load(); }} />
      ) : (
        <>
          <p className="eyebrow mb-2">Tribunal</p>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl flex items-center gap-3"><Scale className="h-8 w-8 text-[#E61A27]" /> Adjudication Docket</h1>
          <p className="mt-3 max-w-2xl text-slate-500">Conflict-free adjudication of disputed submissions. Decisions are binding and recorded immutably.</p>
          <div className="mt-10 space-y-3">
            {cases.map((c) => (
              <button key={c.id} data-testid={`case-${c.id}`} onClick={() => setActive(c.id)} className="axiom-card flex w-full items-center justify-between p-5 text-left">
                <div><p className="text-sm text-slate-900">{c.problem_title}</p><p className="text-xs text-slate-500">{c.reason}</p></div>
                <span className={`rounded-full border px-3 py-1 text-xs font-mono ${c.status === "open" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-950/60 text-emerald-700"}`}>{c.status === "open" ? "Open" : "Decided"}</span>
              </button>
            ))}
            {cases.length === 0 && <p className="text-slate-500" data-testid="tribunal-empty">No cases on the docket. Cases appear when two reviews conflict.</p>}
          </div>
        </>
      )}
    </div>
  );
}
